/* eslint-disable complexity */
import { ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import Card from "@/components/wpl/card/src";
import { DEFAULT_VALUES } from "@/libraries/web-framework";

const ProjectFlexibilitySection = () => {
    const intl = useIntl();
    const body = intl.formatMessage(
        {
            id: "JiraSoftwareFeaturePageTemplate.additional-resources.atlassian-community.body",
            defaultMessage:
                "Choose from team or company-managed projects. Jira fits around your workflows."
        },
        { ...DEFAULT_VALUES }
    );

    return (
        <section
            css={css({
                padding: "32px 0 16px"
            })}
        >
            <h2
                css={css({
                    color: "#25385A",
                    margin: "0",
                    padding: "16px 16px 36px",
                    textAlign: "center",
                    fontWeight: "500",

                    [media.below.sm]: {
                        fontSize: `${token("space.300")}`,
                        paddingBlockEnd: "30px"
                    },

                    [media.above.sm]: {
                        fontSize: "2.25rem"
                    }
                })}
            >
                {intl.formatMessage({
                    id: "JiraSoftwareFeaturePageTemplate.additional-resources.header",
                    defaultMessage: "Project flexibility"
                })}
            </h2>
            <div
                css={css({
                    display: "flex",
                    flexWrap: "wrap",
                    maxWidth: "1200px",
                    margin: "auto"
                })}
            >
                <ProjectFlexibilityCard
                    headerText={intl.formatMessage({
                        id: "JiraSoftwareFeaturePageTemplate.additional-resources.atlassian-community.header",
                        defaultMessage: "Autonomy and structure"
                    })}
                    bodyText={body as string}
                    imageLink="https://wac-cdn.atlassian.com/dam/jcr:b6be290e-c637-4462-9013-b5d17e6920c7/illustrations-spot-Simplify.svg?cdnVersion=1398"
                    imageAltText={intl.formatMessage({
                        id: "JiraSoftwareFeaturePageTemplate.additional-resources.atlassian-community.image-alt-text",
                        defaultMessage: "Simplify illustration"
                    })}
                />
                <ProjectFlexibilityCard
                    headerText={intl.formatMessage({
                        id: "JiraSoftwareFeaturePageTemplate.additional-resources.atlassian-university.header",
                        defaultMessage: "Customizable workflows"
                    })}
                    bodyText={intl.formatMessage({
                        id: "JiraSoftwareFeaturePageTemplate.additional-resources.atlassian-university.body",
                        defaultMessage:
                            "Create customizable workflows that map to any style of work."
                    })}
                    imageLink="https://wac-cdn.atlassian.com/dam/jcr:78d20ac0-6ac0-4b58-961a-7d853b3c14dc/illustrations-spot-Complex%20Workflow.svg?cdnVersion=1398"
                    imageAltText={intl.formatMessage({
                        id: "JiraSoftwareFeaturePageTemplate.additional-resources.atlassian-university.image-alt-text",
                        defaultMessage: "Complex workflow illustration"
                    })}
                />
                <ProjectFlexibilityCard
                    headerText={intl.formatMessage({
                        id: "JiraSoftwareFeaturePageTemplate.additional-resources.support.header",
                        defaultMessage: "Extend with apps and integrations"
                    })}
                    bodyText={intl.formatMessage(
                        {
                            id: "JiraSoftwareFeaturePageTemplate.additional-resources.support.body",
                            defaultMessage:
                                "Extend the power of Jira Software. 3000+ apps and integrations available. <a>Learn More</a>"
                        },
                        {
                            a: (node: ReactNode) => (
                                <a
                                    data-event="clicked"
                                    data-event-component="linkButton"
                                    data-event-container="imageHeadingTextBlock"
                                    data-product-key="jiraSoftwareFeature"
                                    data-label="Learn more"
                                    href="https://marketplace.atlassian.com/"
                                >
                                    {node}
                                </a>
                            )
                        }
                    )}
                    imageLink="https://wac-cdn.atlassian.com/dam/jcr:76e94ebd-d71e-4991-9008-7b2e838090c8/illustrations-spot-Integrations.svg?cdnVersion=1398"
                    imageAltText={intl.formatMessage({
                        id: "JiraSoftwareFeaturePageTemplate.additional-resources.support.image-alt-text",
                        defaultMessage: "Integrations illustration"
                    })}
                />
            </div>
        </section>
    );
};

interface ProjectFlexibilityCardProps {
    headerText: string;
    bodyText: string | ReactNode;
    imageLink: string;
    imageAltText: string;
}

const ProjectFlexibilityCard = (props: ProjectFlexibilityCardProps) => {
    return (
        <div
            css={css({
                "paddingBlockStart": "16px",
                "width": "33.33%",

                "div": {
                    boxShadow: "unset",
                    backgroundColor: "transparent"
                },
                "p": {
                    textAlign: "center",
                    fontSize: `${token("space.200")}`,
                    lineHeight: `${token("space.250")}`
                },

                "img": {
                    height: "92px",
                    objectFit: "contain"
                },

                ".contentBlock-linkFlex": {
                    justifyContent: "center",
                    margin: "0"
                },

                [media.only.sm]: {
                    width: "50%"
                },

                [media.below.sm]: {
                    width: "100%",
                    paddingBlockStart: "0px"
                },

                ".text-section": {
                    [media.below.sm]: {
                        padding: "16px",
                        paddingBlockStart: "0px"
                    },
                    p: {
                        [media.below.sm]: {
                            marginBlockEnd: "1px"
                        }
                    },
                    h4: {
                        [media.below.sm]: {
                            fontSize: `${token("space.200")}`,
                            marginBlockEnd: "6px",
                            textAlign: "center",
                            color: "#253858",
                            lineHeight: "1.25",
                            letterSpacing: ".3px"
                        },
                        [media.above.sm]: {
                            fontSize: `${token("space.250")}`,
                            marginBlockEnd: "6px",
                            textAlign: "center",
                            color: "#253858"
                        }
                    },
                    a: {
                        "color": "#0052cc",
                        "verticalAlign": "baseline",
                        "fontWeight": "400",
                        "textDecoration": "underline",
                        "&:hover": {
                            textDecoration: "none"
                        }
                    }
                }
            })}
        >
            <Card
                headerType="h4"
                headerText={props.headerText}
                bodyText={props.bodyText}
                imageLink={props.imageLink}
                imageAltText={props.imageAltText}
                aspectRatio="none"
            />
        </div>
    );
};

export default ProjectFlexibilitySection;
